import type { ProblemeConfig } from '@/lib/api/config';
import { Alert, Logo } from '@pms-digital/design-system';

interface EcranErreurConfigurationProps {
  problemes: readonly ProblemeConfig[];
}

/**
 * The page shown instead of the application when its public configuration is absent or
 * invalid (`lib/api/config.ts`): a readable list of what to fix, never a blank page. It
 * stands on its own (no navbar, no router, no session), in the style of the login panel.
 */
export function EcranErreurConfiguration({ problemes }: EcranErreurConfigurationProps) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12">
      <div className="w-full max-w-[640px]">
        <section className="rounded-lg bg-white p-12 shadow-lg">
          <div className="mb-8 flex justify-center">
            <Logo width={224} />
          </div>
          <h1 className="text-center font-bold text-h2">Configuration incomplète</h1>
          <p className="mt-2 text-center font-light text-body-lg text-gray-700">
            L'application ne peut pas démarrer.
          </p>

          <div className="mt-8">
            <Alert
              tone="danger"
              title={
                problemes.length > 1
                  ? 'Ces variables de configuration sont à corriger :'
                  : 'Cette variable de configuration est à corriger :'
              }
            >
              <ul className="mt-2 space-y-1">
                {problemes.map((probleme) => (
                  <li key={probleme.variable}>
                    <code className="font-semibold">{probleme.variable}</code>
                    {' : '}
                    {probleme.message}
                  </li>
                ))}
              </ul>
            </Alert>
          </div>

          <p className="mt-6 text-body text-gray-700">
            Ces valeurs propres à l'environnement Tahiti ne sont jamais inscrites dans le code. En
            local, renseignez-les dans <code className="font-semibold">packages/frontend/.env</code>{' '}
            (voir <code className="font-semibold">.env.example</code>) puis relancez Vite. Sur un
            serveur, ce sont les variables du job de déploiement (voir{' '}
            <code className="font-semibold">docs/deploy.md</code>) : corrigez-les puis redéployez.
          </p>
        </section>
        <p className="mt-6 text-center text-gray-700 text-small">
          Besoin d'aide ? Contactez votre interlocuteur chez patrickmathieu singularité.
        </p>
      </div>
    </main>
  );
}
